export type TypeItemCategory4GA4 = 'Smiles' | 'Money' | 'Smiles&Money' | '<vazio>';

const DATALAYER_REGION = '@smiles/datalayer-region';

interface Product {
  itemName: string;
  itemId: string;
  price: number;
  miles: number;
  itemBrand: string;
  itemCategory: 'HOTEIS-Earn' | 'HOTEIS-Burn' | '<vazio>';
  itemVariant: string;
  itemCategory4: TypeItemCategory4GA4;
  quantityDays: number;
  prevQuantityDays: number;
  itemTravellers: string;
  index: number;
  quantity: number;
}

interface Payment {
  value: number;
  valueMiles: number;
  taxMiles: number;
  taxMoney: number;
}

type ProdType = 'HOTEIS/CLUBE' | 'HOTEIS';

export interface IGA4 {
  addToCart: {
    prodType: ProdType;
    isCrossSellChecked?: boolean;
    params: {
      product: Product;
      payment: Payment;
    };
  };

  viewSearch: {
    prodType: ProdType;
    isCrossSellChecked?: boolean;
  };

  productDetail: {
    prodType: ProdType;
    isCrossSellChecked?: boolean;
    params: {
      product: Product;
      payment: Payment;
    };
  };

  beginCheckout: {
    prodType: ProdType;
    isCrossSellChecked?: boolean;
    params: {
      product: Product;
      payment: Payment;
    };
  };
}

const EVENTS = {
  ADD_TO_CART: 'add_to_cart',
  VIEW_SEARCH: 'view_search',
  PRODUCT_DETAIL: 'product_detail',
  BEGIN_CHECKOUT: 'begin_checkout',
};

const emptyItems = () => {
  window.dataLayer.push({ ecommerce: null });
};

const addToCart = ({ prodType, isCrossSellChecked = false, params: { product, payment } }: IGA4['addToCart']) => {
  const crossSell = isCrossSellChecked ? 'Sim' : 'Não';
  const searchTerm = String(window.sessionStorage.getItem(DATALAYER_REGION));

  window.dataLayer.push({
    event: EVENTS.ADD_TO_CART,
    prod_type: prodType,
    cross_sell: crossSell,
    search_term: searchTerm,
    value: payment.value,
    value_milhas: payment.valueMiles,
    tax_miles: payment.taxMiles,
    tax_money: payment.taxMoney,
    currency: 'BRL',
    step: 2,
    ecommerce: {
      items: [
        {
          item_name: `HOTEIS-${product.itemName}`,
          item_id: `HOTEIS-${product.itemId}`,
          price: 0,
          miles: 0,
          item_brand: `HOTEIS-${product.itemBrand}`,
          item_category: product.itemCategory !== '<vazio>' ? `HOTEIS-${product.itemCategory}` : product.itemCategory,
          item_variant: product.itemVariant,
          item_category4: product.itemCategory4,
          quantity_days: product.quantityDays,
          prev_quantity_days: product.prevQuantityDays,
          item_travellers: product.itemTravellers,
          index: product.index,
          quantity: product.quantity,
        },
      ],
    },
  });
};

const viewSearch = ({ prodType, isCrossSellChecked = false }: IGA4['viewSearch']) => {
  const crossSell = isCrossSellChecked ? 'Sim' : 'Não';
  const searchTerm = String(window.sessionStorage.getItem(DATALAYER_REGION));

  window.dataLayer.push({
    event: EVENTS.VIEW_SEARCH,
    prod_type: prodType,
    cross_sell: crossSell,
    search_term: searchTerm,
    step: 1,
    value: 0,
    value_milhas: 0,
    tax_miles: 0,
    tax_money: 0,
    currency: 'BRL',
    ecommerce: {
      items: [{}],
    },
  });
};

const productDetail = ({
  prodType,
  isCrossSellChecked = false,
  params: { payment, product },
}: IGA4['productDetail']) => {
  const crossSell = isCrossSellChecked ? 'Sim' : 'Não';
  const searchTerm = String(window.sessionStorage.getItem(DATALAYER_REGION));

  window.dataLayer.push({
    event: EVENTS.PRODUCT_DETAIL,
    prod_type: prodType,
    cross_sell: crossSell,
    search_term: searchTerm,
    value: payment.value,
    value_milhas: payment.valueMiles,
    tax_miles: payment.taxMiles,
    tax_money: payment.taxMoney,
    currency: 'BRL',
    step: 2,
    ecommerce: {
      items: [
        {
          item_name: `HOTEIS-${product.itemName}`,
          item_id: `HOTEIS-${product.itemId}`,
          price: product.price,
          miles: product.miles,
          item_brand: product.itemBrand !== '<vazio>' ? `HOTEIS-${product.itemBrand}` : product.itemBrand,
          item_category: product.itemCategory !== '<vazio>' ? `HOTEIS-${product.itemCategory}` : product.itemCategory,
          item_variant: product.itemVariant,
          item_category4: product.itemCategory4,
          quantity_days: product.quantityDays,
          prev_quantity_days: product.prevQuantityDays,
          item_travellers: product.itemTravellers,
          index: product.index,
          quantity: product.quantity,
        },
      ],
    },
  });
};

const beginCheckout = ({ prodType, isCrossSellChecked, params: { product, payment } }: IGA4['beginCheckout']) => {
  const crossSell = isCrossSellChecked ? 'Sim' : 'Não';
  const searchTerm = String(window.sessionStorage.getItem(DATALAYER_REGION));

  window.dataLayer.push({
    event: EVENTS.BEGIN_CHECKOUT,
    prod_type: prodType,
    cross_sell: crossSell,
    search_term: searchTerm,
    value: payment.value,
    value_milhas: 0,
    tax_miles: payment.taxMiles,
    tax_money: payment.taxMoney,
    currency: 'BRL',
    step: 3,
    ecommerce: {
      items: [
        {
          item_name: `HOTEIS-${product.itemName}`,
          item_id: `HOTEIS-${product.itemId}`,
          price: product.price,
          miles: product.miles,
          item_brand: `HOTEIS-${product.itemBrand}`,
          item_category: product.itemCategory !== '<vazio>' ? `HOTEIS-${product.itemCategory}` : product.itemCategory,
          item_variant: product.itemVariant,
          item_category4: product.itemCategory4,
          quantity_days: product.quantityDays,
          prev_quantity_days: product.prevQuantityDays,
          item_travellers: product.itemTravellers,
          index: product.index,
          quantity: product.quantity,
        },
      ],
    },
  });
};

export const GA4 = {
  emptyItems,
  addToCart,
  viewSearch,
  productDetail,
  beginCheckout,
  DATALAYER_REGION,
};
